import { ApiError } from "../../../core/lib/api";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  TONE_LABELS,
  TRANSFER_POLICY_LABELS,
  WEEKDAY_LABELS,
  type AppelsCapabilities,
  type AppelsSettings,
  type AppelsStats,
  type Weekday,
} from "@shared/modules/x-appels";
import { Badge } from "../../../core/components/ui/badge";
import { Button } from "../../../core/components/ui/button";
import { Card } from "../../../core/components/ui/card";
import { Icon } from "../../../core/components/ui/icon";
import { Field, Input, Select, Textarea } from "../../../core/components/ui/input";
import { PageHeader } from "../../../core/components/ui/page-header";
import { Toggle } from "../../../core/components/ui/toggle";
import { useToastStore } from "../../../core/stores/toast";
import { SupervisionTokenCard } from "../components/supervision-token";
import { VoiceModeSelector } from "../components/voice-mode-selector";
import { getCapabilities, getSettings, getStats, simulateCall, updateSettings } from "../lib/api";

const DAYS = Object.keys(WEEKDAY_LABELS) as Weekday[];

/**
 * Réglages du répondeur IA : qui répond et comment, quand l'atelier est
 * ouvert, ce que l'assistant a le droit de faire, ses consignes, et qui
 * elle prévient. Un seul bouton Enregistrer, en tête et en pied.
 */
export function RepondeurPage() {
  const navigate = useNavigate();
  const push = useToastStore((s) => s.push);
  const [draft, setDraft] = useState<AppelsSettings | null>(null);
  const [stats, setStats] = useState<AppelsStats | null>(null);
  const [busy, setBusy] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [capabilities, setCapabilities] = useState<AppelsCapabilities | null>(null);
  const femaleVoiceConfigured = capabilities?.femaleVoiceConfigured ?? false;

  useEffect(() => {
    void getCapabilities()
      .then(setCapabilities)
      .catch(() => {});
    getSettings()
      .then(setDraft)
      .catch(() => push("danger", "Impossible de charger les réglages"));
    getStats()
      .then(setStats)
      .catch(() => setStats(null));
  }, [push]);

  const set = <K extends keyof AppelsSettings>(key: K, value: AppelsSettings[K]) => {
    setDraft((d) => (d ? { ...d, [key]: value } : d));
    setDirty(true);
  };

  const setDay = (day: Weekday, value: AppelsSettings["hours"][Weekday]) => {
    if (!draft) return;
    set("hours", { ...draft.hours, [day]: value });
  };

  const save = async () => {
    if (!draft || busy) return;
    setBusy(true);
    try {
      setDraft(await updateSettings(draft));
      setDirty(false);
      push(
        "success",
        "Répondeur mis à jour",
        "Réglages enregistrés. Le moteur vocal les lit au début de chaque appel.",
      );
    } catch (error) {
      const modeUnavailable =
        error instanceof ApiError &&
        ["voice_mode_not_available", "voice_mode_preprod_only"].includes(error.code);
      if (modeUnavailable)
        void getCapabilities()
          .then(setCapabilities)
          .catch(() => {});
      push(
        "danger",
        "Enregistrement impossible",
        modeUnavailable
          ? "Cet assistant n’est pas disponible pour les essais. Choisissez l’assistant actuel."
          : "Vérifiez les heures (HH:MM) et le courriel.",
      );
    } finally {
      setBusy(false);
    }
  };

  const simulate = async () => {
    try {
      await simulateCall();
      push("info", "Appel simulé lancé");
      navigate("/appels");
    } catch {
      push("danger", "Simulation impossible");
    }
  };

  const saveButton = (
    <Button variant="primary" disabled={busy || !draft || !dirty} onClick={() => void save()}>
      {dirty ? "Enregistrer" : "Enregistré"}
    </Button>
  );

  if (!draft) {
    return (
      <div className="page">
        <PageHeader title="Répondeur IA" />
        <p className="empty">Chargement…</p>
      </div>
    );
  }

  return (
    <div className="page repondeur">
      <PageHeader
        title="Répondeur IA"
        subtitle="Votre assistant téléphonique, disponible 24 h/24 et 7 j/7."
        actions={saveButton}
      />

      <div className="repondeur__grid">
        {capabilities?.voiceModes?.environment === "preprod" ? (
          <VoiceModeSelector
            value={draft.voiceMode}
            capabilities={capabilities.voiceModes}
            disabled={busy}
            onChange={(mode) => set("voiceMode", mode)}
          />
        ) : null}
        <Card
          title="Activation"
          subtitle="L’IA répond aux appels reçus sur son numéro, même lorsque l’atelier est fermé."
        >
          <div className="repondeur__stack">
            <Toggle
              checked={draft.enabled}
              onChange={(v) => set("enabled", v)}
              label={draft.enabled ? "Répondeur IA activé" : "Répondeur IA désactivé"}
              hint={
                draft.enabled
                  ? "L’IA répond à toute heure et réserve les créneaux disponibles dans l’agenda."
                  : "Les appels non répondus tombent sur la boîte vocale classique."
              }
            />
            <div className="form-grid">
              <Field label="Prénom de l'assistant" hint="Il se présente ainsi au téléphone.">
                <Input
                  value={draft.assistantName}
                  onChange={(e) => set("assistantName", e.target.value)}
                />
              </Field>
              <Field label="Voix">
                <Select
                  value={draft.voice}
                  onChange={(e) => set("voice", e.target.value as AppelsSettings["voice"])}
                >
                  <option value="feminine" disabled={!femaleVoiceConfigured}>
                    {femaleVoiceConfigured ? "Féminine" : "Féminine — à configurer"}
                  </option>
                  <option value="masculine">Masculine</option>
                </Select>
              </Field>
              <Field label="Langue">
                <Select
                  value={draft.language}
                  onChange={(e) => set("language", e.target.value as AppelsSettings["language"])}
                >
                  <option value="fr">Français</option>
                  <option value="fr-en">Français, anglais si l'appelant le parle</option>
                </Select>
              </Field>
              <Field label="Ton">
                <Select
                  value={draft.tone}
                  onChange={(e) => set("tone", e.target.value as AppelsSettings["tone"])}
                >
                  {(Object.keys(TONE_LABELS) as AppelsSettings["tone"][]).map((t) => (
                    <option key={t} value={t}>
                      {TONE_LABELS[t]}
                    </option>
                  ))}
                </Select>
              </Field>
            </div>
          </div>
        </Card>

        <Card
          title="Clients prioritaires et transferts"
          subtitle="Les concessionnaires reconnus joignent directement le gérant."
        >
          <div className="repondeur__stack">
            <Toggle
              checked={draft.priorityDealers}
              onChange={(v) => set("priorityDealers", v)}
              label="Concessionnaires : ligne directe"
              hint="Les numéros des fiches de type Concessionnaire font sonner le gérant sans accueil IA, pendant les heures ci-dessous. Si la ligne est occupée ou sans réponse, l’assistant reprend l’appel."
            />
            <p className="repondeur__note">
              Dans <a href="/clients">Clients</a>, choisissez le type « Concessionnaire » et
              renseignez le numéro depuis lequel le client appelle.
            </p>
            <div className="form-grid">
              <Field label="Autres clients : transférer à un humain">
                <Select
                  value={draft.transferPolicy}
                  onChange={(e) =>
                    set("transferPolicy", e.target.value as AppelsSettings["transferPolicy"])
                  }
                >
                  {(Object.keys(TRANSFER_POLICY_LABELS) as AppelsSettings["transferPolicy"][]).map(
                    (p) => (
                      <option key={p} value={p}>
                        {TRANSFER_POLICY_LABELS[p]}
                      </option>
                    ),
                  )}
                </Select>
              </Field>
              <Field
                label="Ligne directe du gérant"
                hint="Numéro appelé pour les transferts. Utilisez une ligne qui ne renvoie pas vers le répondeur IA."
              >
                <Input
                  type="tel"
                  value={draft.transferNumber}
                  onChange={(e) => set("transferNumber", e.target.value)}
                  placeholder="ex : 418 555-0102"
                />
              </Field>
            </div>
          </div>
        </Card>

        <Card title="Message d'accueil" subtitle="Ce que l'appelant entend en premier.">
          <div className="repondeur__stack">
            <Field
              label="Accueil à toute heure"
              hint="La même voix et le même accueil, de jour comme de nuit."
            >
              <Textarea
                rows={3}
                value={draft.greeting}
                onChange={(e) => set("greeting", e.target.value)}
              />
            </Field>
            <div className="repondeur__preview" aria-label="Aperçu">
              <span className="repondeur__preview-who">
                <Icon name="sparkles" size={14} /> {draft.assistantName}
              </span>
              <p>{draft.greeting || "…"}</p>
            </div>
          </div>
        </Card>

        <Card
          title="Heures d'ouverture"
          subtitle="Disponibilité de l’équipe pour les transferts. L’IA reste joignable 24 h/24 ; les créneaux proposés respectent les horaires et les disponibilités de l’agenda."
        >
          <ul className="hours">
            {DAYS.map((day) => {
              const value = draft.hours[day];
              return (
                <li key={day} className={`hours__day${value ? "" : " hours__day--closed"}`}>
                  <label className="hours__toggle">
                    <input
                      type="checkbox"
                      checked={value !== null}
                      onChange={(e) =>
                        setDay(day, e.target.checked ? { open: "08:00", close: "17:30" } : null)
                      }
                    />
                    <span>{WEEKDAY_LABELS[day]}</span>
                  </label>
                  {value ? (
                    <span className="hours__range">
                      <input
                        type="time"
                        className="input hours__time"
                        aria-label={`${WEEKDAY_LABELS[day]} — ouverture`}
                        value={value.open}
                        onChange={(e) => setDay(day, { ...value, open: e.target.value })}
                      />
                      <span className="hours__sep">à</span>
                      <input
                        type="time"
                        className="input hours__time"
                        aria-label={`${WEEKDAY_LABELS[day]} — fermeture`}
                        value={value.close}
                        onChange={(e) => setDay(day, { ...value, close: e.target.value })}
                      />
                    </span>
                  ) : (
                    <span className="hours__closed">Fermé</span>
                  )}
                </li>
              );
            })}
          </ul>
        </Card>

        <Card
          title="Ce que l'assistant peut faire"
          subtitle="Chaque permission retirée devient un message pris pour l'équipe."
        >
          <div className="repondeur__stack">
            <Toggle
              checked={draft.canBook}
              onChange={(v) => set("canBook", v)}
              label="Prendre des rendez-vous"
              hint="À toute heure, dans les créneaux libres de l’agenda, pour les services du CRM dont le prix et la durée sont connus. Indépendant des réservations du site."
            />
            <Toggle
              checked={draft.canQuotePrices}
              onChange={(v) => set("canQuotePrices", v)}
              label="Donner les prix"
              hint="Seulement ceux marqués « l'agent IA peut communiquer le prix » au catalogue."
            />
            <Toggle
              checked={draft.canTakeMessages}
              onChange={(v) => set("canTakeMessages", v)}
              label="Prendre des messages"
              hint="Nom, numéro, raison de l'appel — visibles dans « À traiter »."
            />
            <Toggle
              checked={draft.canCheckAppointments}
              onChange={(v) => set("canCheckAppointments", v)}
              label="Vérifier un rendez-vous existant"
              hint="Retrouve la date, l’heure et les services d’un rendez-vous déjà pris, une fois le numéro et le nom vérifiés avec la fiche client. Ne modifie ni n’annule jamais rien."
            />
            {/* La présence se confirme sur un rendez-vous retrouvé : elle suit la vérification. */}
            {draft.canCheckAppointments ? (
              <Toggle
                checked={draft.canConfirmPresence}
                onChange={(v) => set("canConfirmPresence", v)}
                label="Confirmer la présence du client"
                hint="Sur sa confirmation explicite : le rendez-vous passe de « réservé » à « confirmé », noté à l’historique."
              />
            ) : null}
            <div className="form-grid">
              <Field label="Durée maximale d'un appel (minutes)">
                <Input
                  type="number"
                  min={1}
                  max={60}
                  value={draft.maxCallMinutes}
                  onChange={(e) => set("maxCallMinutes", Number(e.target.value))}
                />
              </Field>
            </div>
          </div>
        </Card>

        <Card
          title="Consignes"
          subtitle="Ce que l'assistant doit savoir ou ne jamais faire. Une consigne par ligne."
        >
          <Textarea
            rows={7}
            value={draft.instructions}
            onChange={(e) => set("instructions", e.target.value)}
            placeholder="ex : Ne jamais promettre un délai de fin de travaux."
          />
          <p className="repondeur__note">
            Il connaît déjà le catalogue (services, durées, prix autorisés), l'adresse et les heures
            de l'atelier, et l'agenda pour proposer des créneaux.
          </p>
        </Card>

        <Card
          title="Notifications"
          subtitle="Qui est prévenu quand un message est pris ou un appel manqué."
        >
          <div className="form-grid">
            <Field label="Texto à" hint="Vide = aucune alerte par texto.">
              <Input
                type="tel"
                value={draft.notifyPhone}
                onChange={(e) => set("notifyPhone", e.target.value)}
                placeholder="ex : 418 555-0102"
              />
            </Field>
            <Field label="Courriel à" hint="Vide = aucune alerte par courriel.">
              <Input
                type="email"
                value={draft.notifyEmail}
                onChange={(e) => set("notifyEmail", e.target.value)}
                placeholder="ex : atelier@protecto.ca"
              />
            </Field>
          </div>
          <div className="form-grid">
            <Field
              label="Cellulaire du propriétaire"
              hint="Reçoit un texto pour chaque demande de rappel ou d’annulation prise par Léo (nom, numéro, rendez-vous, raison), pour agir sans ouvrir le CRM. Distinct de la ligne de l’entreprise."
            >
              <Input
                type="tel"
                value={draft.ownerMobile ?? ""}
                onChange={(e) => set("ownerMobile", e.target.value)}
                placeholder="ex : 418 555-0199"
              />
            </Field>
          </div>
          {!(draft.ownerMobile ?? "").trim() ? (
            <p className="reglages__note" role="status">
              Sans cellulaire du propriétaire, les demandes de rappel et d’annulation de Léo restent
              au CRM (Appels → Demandes) sans texto.
            </p>
          ) : null}
          {!draft.notifyPhone.trim() && !draft.notifyEmail.trim() ? (
            <p className="reglages__note" role="status">
              Aucun destinataire : personne n'est prévenu après un appel. Renseignez un numéro ou un
              courriel — l'envoi passe ensuite par la messagerie, qui doit elle-même avoir ses clés
              Twilio / Mailjet (Messages → Réglages).
            </p>
          ) : null}
        </Card>

        <Card title="Téléphonie" subtitle="État du branchement avec la centrale téléphonique.">
          <dl className="kv">
            <dt>Répondeur</dt>
            <dd>
              <Badge variant={draft.enabled ? "success" : "neutral"}>
                {draft.enabled ? "Actif" : "Inactif"}
              </Badge>
            </dd>
            <dt>Appels cette semaine</dt>
            <dd>{stats ? stats.weekTotal : "—"}</dd>
            <dt>Durée moyenne</dt>
            <dd>
              {stats
                ? `${Math.round(stats.avgDurationSec / 60)} min ${String(stats.avgDurationSec % 60).padStart(2, "0")}`
                : "—"}
            </dd>
          </dl>
          {stats?.simulationAvailable ? (
            <div className="repondeur__test">
              <Button
                variant="ghost"
                icon={<Icon name="play" size={16} />}
                onClick={() => void simulate()}
              >
                Simuler un appel
              </Button>
              <span className="repondeur__note">
                Joue un scénario comme si un client appelait : l'appel apparaît en direct dans «
                Appels ».
              </span>
            </div>
          ) : null}
        </Card>

        <SupervisionTokenCard />
      </div>
    </div>
  );
}
